import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CLIP_LIBRARY_OFFLINE_MESSAGE,
  type CampaignClipsResponse,
  type ClipLinkResponse,
  type LibraryClip,
} from '@nexus/shared';

import { ApiError, apiGet, apiSend } from '../lib/api';
import { Alert, Button, Card, EmptyState, SearchField, Skeleton } from '../ui';

/**
 * "Clips to download": the ready-made clips Forge cut for this campaign.
 *
 * Mounted on the campaign page for a creator who has JOINED, beneath the
 * reference material. Self-contained on purpose — it fetches its own data and
 * owns its own states — so the page it sits on knows nothing about the clip
 * library and cannot be broken by it:
 *
 *   not linked   the campaign has no folder: the section is simply absent;
 *   offline      the library did not answer: one sentence, nothing else;
 *   empty        the folder is linked and has nothing in it yet;
 *   clips        a grid of cards with search by title, a copy-caption action
 *                and a Download button that mints a fifteen-minute link.
 *
 * A Download is two steps — ask the Worker for a signed link, then send the
 * browser to it — because the link is minted per click and recorded per
 * click: that row is the evidence trail. `Content-Disposition: attachment`
 * on the far end means the page stays where it is.
 */

/**
 * Where a download goes. One function on an object so a test can replace it:
 * jsdom has no `location.assign`, and the page must not open a new window (a
 * popup blocker would eat the download; a same-tab navigation to an
 * `attachment` response leaves the page where it is).
 */
export const libraryNav = {
  go: (url: string): void => {
    window.location.assign(url);
  },
};

type State =
  | { kind: 'loading' }
  | { kind: 'hidden' }
  | { kind: 'offline' }
  | { kind: 'ok'; clips: LibraryClip[]; linkTtlMinutes: number };

export function CampaignLibrarySection({ slug }: { slug: string }): React.JSX.Element | null {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    try {
      const body = await apiGet<CampaignClipsResponse>(`/campaigns/${encodeURIComponent(slug)}/clips`);
      if (body.status === 'ok') setState({ kind: 'ok', clips: body.clips, linkTtlMinutes: body.linkTtlMinutes });
      else if (body.status === 'offline') setState({ kind: 'offline' });
      else setState({ kind: 'hidden' });
    } catch (err) {
      /* A 401/403/404 is "not for you" or "not here" and the section goes
         away; anything else is the library's problem, not the page's. */
      if (err instanceof ApiError && [401, 403, 404].includes(err.status)) setState({ kind: 'hidden' });
      else setState({ kind: 'offline' });
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => {
    if (state.kind !== 'ok') return [];
    const needle = query.trim().toLowerCase();
    if (!needle) return state.clips;
    return state.clips.filter((clip) => clipTitle(clip).toLowerCase().includes(needle));
  }, [state, query]);

  if (state.kind === 'hidden') return null;

  if (state.kind === 'loading') {
    return (
      <Card title="Clips to download" tight>
        <Skeleton lines={3} />
      </Card>
    );
  }

  if (state.kind === 'offline') {
    return (
      <Card title="Clips to download" tight>
        <Alert tone="waiting">{CLIP_LIBRARY_OFFLINE_MESSAGE}</Alert>
      </Card>
    );
  }

  if (state.clips.length === 0) {
    return (
      <Card title="Clips to download" tight>
        <EmptyState title="No clips in the folder yet">
          Forge is still cutting. Check back soon — they land here the moment they are ready.
        </EmptyState>
      </Card>
    );
  }

  return (
    <Card
      title="Clips to download"
      sub={`${state.clips.length} ready-made clip${state.clips.length === 1 ? '' : 's'}, cut from this campaign's source. Download one, post it from your account, and submit the link as usual.`}
      tight
      id="clips"
    >
      <SearchField
        label="Search clips by title"
        visuallyHiddenLabel
        placeholder="Search by title"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        maxLength={200}
      />
      {shown.length === 0 ? (
        <p className="muted" data-testid="clip-search-empty">
          No clip title matches “{query.trim()}”.
        </p>
      ) : (
        <ul className="clip-grid" aria-label="Clips">
          {shown.map((clip) => (
            <ClipCard key={clip.fileName} slug={slug} clip={clip} linkTtlMinutes={state.linkTtlMinutes} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ClipCard({
  slug,
  clip,
  linkTtlMinutes,
}: {
  slug: string;
  clip: LibraryClip;
  linkTtlMinutes: number;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const caption = captionOf(clip);

  async function download(): Promise<void> {
    setBusy(true);
    setProblem(null);
    try {
      const link = await apiSend<ClipLinkResponse>('POST', `/campaigns/${encodeURIComponent(slug)}/clips/link`, {
        fileName: clip.fileName,
      });
      libraryNav.go(link.url);
    } catch (err) {
      setProblem(err instanceof ApiError ? err.message : 'Could not get a download link.');
    } finally {
      setBusy(false);
    }
  }

  async function copyCaption(): Promise<void> {
    if (!caption) return;
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setProblem('Could not copy — select the caption and copy it by hand.');
    }
  }

  return (
    <li className="clip-card" data-testid="clip-card">
      <div className="clip-head">
        <h3 className="clip-title">{clipTitle(clip)}</h3>
        <p className="clip-meta">
          {clip.durationSeconds !== null && <span>{formatDuration(clip.durationSeconds)}</span>}
          <span>{formatSize(clip.sizeBytes)}</span>
        </p>
      </div>
      {caption ? (
        <p className="clip-caption" title={caption}>
          {caption}
        </p>
      ) : (
        <p className="clip-caption muted">No caption written for this one — write your own.</p>
      )}
      {clip.hashtags.length > 0 && (
        <p className="clip-tags">
          {clip.hashtags.map((tag) => (
            <span key={tag} className="chip quiet">
              #{tag}
            </span>
          ))}
        </p>
      )}
      <p className="clip-file muted" title={clip.fileName}>
        {clip.fileName}
      </p>
      <div className="actions clip-actions">
        <Button variant="primary" small onClick={() => void download()} disabled={busy}>
          {busy ? 'Getting link…' : 'Download'}
        </Button>
        {caption && (
          <Button small onClick={() => void copyCaption()} aria-live="polite">
            {copied ? 'Copied' : 'Copy caption'}
          </Button>
        )}
      </div>
      {problem && (
        <p className="field-error" role="alert">
          {problem}
        </p>
      )}
      <p className="sr-only">Download links are good for {linkTtlMinutes} minutes.</p>
    </li>
  );
}

/** The title Forge wrote, or the file name without its extension. */
export function clipTitle(clip: LibraryClip): string {
  return clip.title?.trim() || clip.fileName.replace(/\.mp4$/i, '');
}

/**
 * What "Copy caption" copies: the ready-to-post text when Forge wrote one,
 * else the description plus the hashtags, else nothing.
 */
export function captionOf(clip: LibraryClip): string | null {
  const post = clip.postText?.trim();
  if (post) return post;
  const description = clip.caption?.trim() ?? '';
  const tags = clip.hashtags.map((t) => `#${t}`).join(' ');
  const joined = [description, tags].filter(Boolean).join('\n\n');
  return joined || null;
}

export function formatDuration(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}
